import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import {
    Add as AddIcon
} from '@mui/icons-material';
import { useAtom } from 'jotai';
import { fetchLaydownData } from '../../../atoms/logistics/LogisticsManagementAtom';
import { createLaydown } from '../../../services/logistics';

function LaydownSvg({
    currentProject,
    scopeLaydownProgress,
    currentLaydown,
    setCurrentLaydown,
    laydownList,
    svgMap,
    blocks,
    enableAddNewLaydown = false,
    setEnabelAddNewLaydown
}) {
    const [laydown, setLaydown] = useState({ boolean: false, enableAdd: enableAddNewLaydown });
    const [addLayoutModal, setAddLayoutModal] = useState({
        boolean: false,
        data: { name: "", x: null, y: null }
    });
    const [svgScale, setSvgScale] = useState(0.8);
    const [svgPosition, setSvgPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const [, setFetchLaydown] = useAtom(fetchLaydownData);

    useEffect(() => {
        svgBlocksSelect();
        // eslint-disable-next-line
    }, [svgMap, blocks]);

    useEffect(() => {
        // Apply scope colors when currentLaydown changes
        if (currentLaydown && scopeLaydownProgress) {
            applyScopeColors();
        } else {
            // Clear colors when no laydown is selected
            clearScopeColors();
        }
        // eslint-disable-next-line
    }, [currentLaydown, scopeLaydownProgress]);

    useEffect(() => {
        // Show laydown labels on initial load
        if (laydownList && laydownList.length > 0 && svgMap) {
            setTimeout(() => {
                showLaydownLabels();
            }, 100);
        }
        // eslint-disable-next-line
    }, [laydownList, svgMap]);

    const svgBlocksSelect = useCallback(() => {
        if (blocks && blocks.length > 0) {
            // eslint-disable-next-line
            let block_svg_id;
            let block_progress_id;

            blocks.forEach((block) => {
                block_svg_id = document.getElementById(
                    handleCheckZimmerman() ? block?.name : block?.block_svg_id
                );
                block_progress_id = document.getElementById(block?.progress_svg_id);

                if (block_progress_id) {
                    const tspan = block_progress_id.querySelector("tspan");
                    if (tspan) {
                        tspan.innerHTML = ``;
                    }
                }
            });
        }
        // eslint-disable-next-line
    }, [svgMap, blocks]);

    const applyScopeColors = () => {
        if (!currentLaydown || !scopeLaydownProgress) return;

        Object.entries(scopeLaydownProgress).forEach(([blockId, data]) => {
            const block_svg_id = document.getElementById(blockId);
            if (block_svg_id && data.color) {
                block_svg_id.style.fill = data.color;
                block_svg_id.style.stroke = data.color;
            }
        });
    };

    const clearScopeColors = () => {
        if (blocks && blocks.length > 0) {
            blocks.forEach((block) => {
                const block_svg_id = document.getElementById(
                    handleCheckZimmerman() ? block?.name : block?.block_svg_id
                );
                if (block_svg_id) {
                    block_svg_id.style.fill = '';
                    block_svg_id.style.stroke = '';
                }
            });
        }
    };

    const showLaydownLabels = () => {
        if (laydownList && laydownList.length > 0) {
            laydownList.forEach((key) => {
                const svgParent = document.getElementsByClassName("svgParentDiv")[0];
                if (svgParent && key.latitude != null && key.longitude != null) {
                    // Remove existing label if it exists
                    const existingElements = document.getElementsByClassName(`${key.id}__${key.name}__name`);
                    Array.from(existingElements).forEach(el => el.remove());

                    // Create dot element
                    const dotElement = document.createElement('div');
                    dotElement.setAttribute("style",
                        `position: absolute; 
             top: ${key.longitude}%; 
             left: ${key.latitude}%; 
             width: 8px;
             height: 8px;
             background-color: #F16253;
             border-radius: 50%;
             transform: translate(-50%, -50%);
             z-index: 100;
             cursor: pointer;`
                    );

                    // Create label element
                    const layName = document.createElement('div');
                    layName.setAttribute("style",
                        `position: absolute; 
             top: ${key.longitude - 2}%; 
             left: ${key.latitude}%; 
             color: #F16253; 
             font-size: 12px; 
             font-weight: 500;
             pointer-events: auto; 
             z-index: 100; 
             cursor: pointer;
             transform: translate(-50%, -100%);
             white-space: nowrap;`
                    );
                    layName.setAttribute("class", `${key.id}__${key.name}__name`);
                    layName.innerHTML = key.name;

                    // Add click handlers to both dot and label
                    const clickHandler = (e) => {
                        e.stopPropagation();
                        setCurrentLaydown(key);
                    };

                    dotElement.addEventListener('click', clickHandler);
                    layName.addEventListener('click', clickHandler);

                    svgParent.appendChild(dotElement);
                    svgParent.appendChild(layName);
                }
            });
        }
    };

    function handleCheckZimmerman() {
        if (currentProject && currentProject?.type) {
            return true;
        } else {
            return false;
        }
    }

    const handleAddLaydown = () => {
        setLaydown(prev => ({ ...prev, enableAdd: !laydown.enableAdd }));
        setEnabelAddNewLaydown(false)
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const newScale = Math.max(0.1, Math.min(3, svgScale + delta));
        setSvgScale(newScale);
    };

    const handleMouseDown = (e) => {
        if (laydown.enableAdd) {
            handleCoordinates(e);
            return;
        }
        setIsDragging(true);
        setDragStart({ x: e.clientX - svgPosition.x, y: e.clientY - svgPosition.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        setSvgPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleCoordinates = (e) => {
        if (!laydown.enableAdd) return;

        const svgContainer = document.getElementsByClassName("svgParentDiv")[0];
        if (!svgContainer) return;

        const rect = svgContainer.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;

        setLaydown(prev => ({ ...prev, enableAdd: false }));
        setAddLayoutModal({
            boolean: true,
            data: { name: "", x: xPercent, y: yPercent }
        });
    };

    const handleSaveLaydown = async () => {
        try {
            const newLaydown = {
                name: addLayoutModal.data.name,
                latitude: addLayoutModal.data.x,
                longitude: addLayoutModal.data.y,
                project_id: currentProject?.id
            };

            await createLaydown(newLaydown);
            await setFetchLaydown();

            setAddLayoutModal({ boolean: false, data: { name: "", x: null, y: null } });
        } catch (error) {
            console.error("Error creating laydown:", error);
        }
    };

    const handleCloseModal = () => {
        setAddLayoutModal({ boolean: false, data: { name: "", x: null, y: null } });
        setLaydown(prev => ({ ...prev, enableAdd: false }));
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', pr: 4, pb: 1 }}>
                {laydown.enableAdd ? (
                    <Button
                        variant="outlined"
                        onClick={handleAddLaydown}
                        sx={{
                            borderColor: 'grey.500',
                            color: 'grey.700',
                            '&:hover': {
                                borderColor: 'grey.700',
                                backgroundColor: 'grey.100'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddLaydown}
                        sx={{
                            backgroundColor: '#f16253',
                            '&:hover': {
                                backgroundColor: '#d1534b'
                            }
                        }}
                    >
                        Add New Laydown
                    </Button>
                )}
            </Box>

            {/* SVG Container */}
            <Box
                sx={{
                    height: 'calc(100vh - 260px)',
                    width: '100%',
                    cursor: laydown.enableAdd ? 'crosshair' : (isDragging ? 'grabbing' : 'grab'),
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none'
                }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <Box
                    className="svgmap svgParentDiv"
                    sx={{
                        position: 'relative',
                        cursor: laydown.enableAdd ? 'crosshair' : 'inherit',
                        transform: `translate(${svgPosition.x}px, ${svgPosition.y}px) scale(${svgScale})`,
                        transformOrigin: 'center center',
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                        '& svg': {
                            width: '800px', // Fixed width like in first screenshot
                            height: 'auto',
                            display: 'block'
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: svgMap }}
                />
            </Box>

            {/* Add Laydown Modal */}
            <Dialog
                open={addLayoutModal.boolean}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add New Laydown</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Laydown Name"
                        fullWidth
                        variant="outlined"
                        value={addLayoutModal.data.name}
                        onChange={(e) =>
                            setAddLayoutModal(prev => ({
                                ...prev,
                                data: { ...prev.data, name: e.target.value }
                            }))
                        }
                        sx={{ mt: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Position: X: {addLayoutModal.data.x?.toFixed(2)}%, Y: {addLayoutModal.data.y?.toFixed(2)}%
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveLaydown}
                        variant="contained"
                        disabled={!addLayoutModal.data.name.trim()}
                        sx={{
                            backgroundColor: '#f16253',
                            '&:hover': {
                                backgroundColor: '#d1534b'
                            }
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default LaydownSvg;